/**
 * Message input component for sending messages in a session.
 * Modern glass input bar with gradient send button.
 */

import { useMemo, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { Box, Chip, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import { AttachFile as AttachFileIcon, Close as CloseIcon, Send as SendIcon } from '@mui/icons-material';

interface MessageInputProps {
  onSend: (payload: { content: string; file?: File; mediaType?: string }) => void | Promise<void>;
  disabled?: boolean;
  placeholder?: string;
}

type DetectedMediaType = 'image' | 'audio' | 'video' | 'document' | 'unknown';

const MAX_BYTES: Record<Exclude<DetectedMediaType, 'unknown'>, number> = {
  image: 20 * 1024 * 1024,
  audio: 100 * 1024 * 1024,
  video: 500 * 1024 * 1024,
  document: 50 * 1024 * 1024,
};

const detectMediaType = (file: File): DetectedMediaType => {
  const mime = (file.type || '').toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime.startsWith('video/')) return 'video';
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (['pdf', 'docx', 'txt'].includes(ext)) return 'document';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'image';
  if (['mp3', 'wav', 'ogg'].includes(ext)) return 'audio';
  if (['mp4', 'avi', 'mov'].includes(ext)) return 'video';
  return 'unknown';
};

const validateFile = (file: File): { ok: boolean; error?: string; mediaType: DetectedMediaType } => {
  const mediaType = detectMediaType(file);
  if (mediaType === 'unknown') {
    return { ok: false, mediaType, error: 'Unsupported file type. Please attach an image, audio, video, or document.' };
  }
  const limit = MAX_BYTES[mediaType];
  if (file.size > limit) {
    return { ok: false, mediaType, error: `File too large for ${mediaType}. Max ${Math.round(limit / (1024 * 1024))} MB.` };
  }
  return { ok: true, mediaType };
};

/**
 * Modern message input with glassmorphism styling and gradient send button.
 */
export const MessageInput = ({ onSend, disabled = false, placeholder = 'Type your message...' }: MessageInputProps) => {
  const [message, setMessage] = useState('');
  const [attachedFile, setAttachedFile] = useState<File | undefined>(undefined);
  const [attachError, setAttachError] = useState<string | undefined>(undefined);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const attachment = useMemo(() => {
    if (!attachedFile) return undefined;
    const v = validateFile(attachedFile);
    return { ...v, file: attachedFile };
  }, [attachedFile]);

  const handleSend = async () => {
    if (disabled || sending) return;
    const content = message.trim();
    if (!content && !attachedFile) return;

    setSending(true);
    try {
      if (attachedFile) {
        const v = validateFile(attachedFile);
        if (!v.ok) { setAttachError(v.error); setSending(false); return; }
        await onSend({ content, file: attachedFile, mediaType: v.mediaType === 'unknown' ? undefined : v.mediaType });
      } else {
        await onSend({ content });
      }
      setMessage('');
      setAttachedFile(undefined);
      setAttachError(undefined);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleFiles = (files: FileList | File[]) => {
    const arr = Array.from(files);
    if (arr.length === 0) return;
    const file = arr[0];
    const v = validateFile(file);
    if (!v.ok) { setAttachError(v.error); setAttachedFile(undefined); return; }
    setAttachError(undefined);
    setAttachedFile(file);
  };

  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of Array.from(items)) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) handleFiles([file]);
      }
    }
  };

  const canSend = !disabled && !sending && (message.trim().length > 0 || !!attachedFile);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        p: 3,
        borderTop: '1px solid var(--glass-border)',
        background: 'var(--glass-bg)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
      }}
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onDrop={(e) => {
        e.preventDefault(); e.stopPropagation();
        if (disabled) return;
        handleFiles(e.dataTransfer.files);
      }}
    >
      {/* Attachment Preview */}
      {attachedFile && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={`${attachedFile.name} (${Math.round(attachedFile.size / 1024)} KB)`}
            variant="outlined"
            size="small"
            onDelete={() => setAttachedFile(undefined)}
            deleteIcon={<CloseIcon />}
          />
          {attachment?.mediaType && attachment.mediaType !== 'unknown' && (
            <Chip label={attachment.mediaType} size="small" color="primary" variant="outlined" />
          )}
        </Box>
      )}

      {attachError && (
        <Typography variant="caption" color="error">
          {attachError}
        </Typography>
      )}

      {/* Input Row */}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end' }}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          onPaste={handlePaste}
          placeholder={placeholder}
          disabled={disabled}
          variant="outlined"
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 'var(--mui-shape-customBorderRadius-md)',
              backgroundColor: 'rgba(255, 255, 255, 0.02)',
              '&.Mui-focused': {
                boxShadow: 'var(--mui-customShadows-primary-sm)',
              },
            },
          }}
        />

        <input
          ref={fileInputRef}
          type="file"
          hidden
          onChange={(e) => {
            if (!e.target.files) return;
            handleFiles(e.target.files);
            e.target.value = '';
          }}
        />

        <Tooltip title="Attach file">
          <span>
            <IconButton
              disabled={disabled}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                border: '1px solid var(--glass-border)',
                borderRadius: 'var(--mui-shape-customBorderRadius-sm)',
                color: 'var(--mui-palette-text-secondary)',
                '&:hover': {
                  borderColor: 'var(--mui-palette-primary-main)',
                  color: 'var(--mui-palette-primary-main)',
                },
              }}
            >
              <AttachFileIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Send message (Enter)">
          <span>
            <IconButton
              onClick={handleSend}
              disabled={!canSend}
              sx={{
                background: canSend ? 'var(--gradient-primary)' : 'rgba(148, 163, 184, 0.1)',
                color: canSend ? '#fff' : 'var(--mui-palette-text-disabled)',
                borderRadius: 'var(--mui-shape-customBorderRadius-sm)',
                transition: 'all 200ms ease',
                '&:hover': {
                  background: canSend ? 'var(--gradient-primary)' : undefined,
                  filter: canSend ? 'brightness(1.15)' : undefined,
                  boxShadow: canSend ? 'var(--glow-primary)' : undefined,
                },
                '&.Mui-disabled': {
                  color: 'var(--mui-palette-text-disabled)',
                },
              }}
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};
